/**
 * Wave geo · 2026-05-21 — Address autocomplete powered by Google Places.
 *
 * Wraps a plain controlled `<input>` and attaches a Google Places
 * `Autocomplete` instance on mount (lazy-loading the SDK via
 * `loadGoogleMaps`). When the user picks a suggestion, the parsed result
 * (formatted address + lat/lng + place id + CEP/city/state extracted from
 * `address_components`) is delivered through `onPlaceSelected`.
 *
 * Failure mode: if the SDK fails to load (no key, blocked network, key
 * restricted to a different referrer) the component still works as a
 * regular text input — `onChange` keeps firing on every keystroke so the
 * PM is never blocked from typing the address manually.
 *
 * UI: shows a tiny inline spinner in the right-hand affordance slot while
 * the SDK loads (~150KB gzipped on first call). Touch target on the wrapping
 * label stays ≥44px to comply with the project mobile guideline.
 */

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
} from 'react';
import { loadGoogleMaps } from '@/lib/google-maps-loader';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';

export interface SelectedPlace {
  address: string;
  lat: number;
  lng: number;
  placeId: string;
  cep: string | null;
  city: string | null;
  state: string | null;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected?: (place: SelectedPlace) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  error?: string;
  hint?: string;
  autoFocus?: boolean;
  id?: string;
  name?: string;
  'data-testid'?: string;
}

/** Pull a single address component by Google "type" key, short or long. */
function extractComponent(
  components: Array<{ long_name: string; short_name: string; types: string[] }> | undefined,
  type: string,
  variant: 'long' | 'short' = 'long',
): string | null {
  if (!components) return null;
  const match = components.find((c) => c.types.includes(type));
  if (!match) return null;
  return variant === 'long' ? match.long_name : match.short_name;
}

const fieldBase =
  'w-full rounded-xl border bg-surface-2 text-ink placeholder:text-ink-subtle ' +
  'border-line transition-colors duration-160 ease-out-soft ' +
  'focus:border-brand-400 focus:ring-2 focus:ring-brand-400/25 focus:outline-none ' +
  'disabled:opacity-55 disabled:cursor-not-allowed h-11 px-3.5 text-[15px]';

const fieldError = 'border-err focus:border-err focus:ring-err/25';

export function AddressAutocomplete(props: Props): JSX.Element {
  const {
    value,
    onChange,
    onPlaceSelected,
    placeholder,
    className,
    disabled,
    required,
    label,
    error,
    hint,
    autoFocus,
    id,
    name,
  } = props;
  const testId = props['data-testid'];
  const { t } = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!inputRef.current) return;
    let cancelled = false;
    setLoading(true);
    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !inputRef.current) return;
        const ac = new maps.places.Autocomplete(inputRef.current, {
          componentRestrictions: { country: 'br' },
          fields: ['place_id', 'formatted_address', 'geometry', 'address_components'],
          types: ['geocode'],
        });
        autocompleteRef.current = ac;
        ac.addListener('place_changed', () => {
          const place = ac.getPlace();
          const loc = place.geometry?.location;
          if (!loc || !place.place_id || !place.formatted_address) {
            // User pressed Enter on a free-text query the SDK could not
            // resolve. Don't fire onPlaceSelected — caller keeps the raw
            // string from onChange and the backend will geocode it.
            return;
          }
          const lat = loc.lat();
          const lng = loc.lng();
          const address = place.formatted_address;
          const components = place.address_components;
          // CEP comes in as "postal_code". City lives under either
          // "administrative_area_level_2" (BR convention for município) or
          // "locality" — we try both. UF code is the 2-letter short_name
          // of administrative_area_level_1.
          const cep = extractComponent(components, 'postal_code');
          const city =
            extractComponent(components, 'administrative_area_level_2') ??
            extractComponent(components, 'locality');
          const stateUf = extractComponent(
            components,
            'administrative_area_level_1',
            'short',
          );
          onChange(address);
          onPlaceSelected?.({
            address,
            lat,
            lng,
            placeId: place.place_id,
            cep,
            city,
            state: stateUf,
          });
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setFailed(true);
        // eslint-disable-next-line no-console
        console.warn(
          '[AddressAutocomplete] Falling back to plain text input — Google Maps SDK failed to load:',
          err instanceof Error ? err.message : err,
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (autocompleteRef.current) {
        try {
          // Detach listeners — we don't recycle the input so this is mostly
          // hygiene against memory leaks on heavy SPA navigation.
          (window as { google?: { maps?: { event?: { clearInstanceListeners?: (i: unknown) => void } } } })
            .google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
        } catch {
          /* ignore */
        }
        autocompleteRef.current = null;
      }
    };
    // Intentionally only runs once on mount — `onChange`/`onPlaceSelected`
    // are read from refs of the closure (latest props are picked up via
    // closure-over-state below), and re-attaching Autocomplete on every
    // keystroke would thrash the SDK.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // pac-container (Google's dropdown) is portaled to body. Make sure it
  // sits above any modal/drawer overlays the app uses (z-50). Style is
  // global, idempotent — added once.
  useEffect(() => {
    const styleId = '__gmaps_pac_zfix';
    if (document.getElementById(styleId)) return;
    const s = document.createElement('style');
    s.id = styleId;
    // Pull the Google suggestion dropdown above modals (z-50 in this app)
    // and align radii with the rest of the surface for visual consistency.
    s.textContent =
      '.pac-container{z-index:9999 !important;border-radius:0.75rem;font-family:inherit;}';
    document.head.appendChild(s);
  }, []);

  const inputId = id ?? name;
  const wrapperStyle: CSSProperties | undefined = undefined;

  return (
    <label className="block space-y-1.5" htmlFor={inputId} style={wrapperStyle}>
      {label && (
        <span className="text-sm font-medium text-ink">
          {label}
          {required && <span className="text-err"> *</span>}
        </span>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          name={name}
          type="text"
          autoComplete="off"
          // P0 anti-bots: the official Places guidance recommends disabling
          // password-manager hijacking on the address input. `1p-ignore` &
          // friends are non-standard but widely respected.
          data-1p-ignore
          data-lpignore="true"
          value={value}
          onChange={(e) => onChange(e.currentTarget.value)}
          placeholder={placeholder ?? t.maps.addressPlaceholder}
          disabled={disabled}
          required={required}
          autoFocus={autoFocus}
          data-testid={testId}
          aria-invalid={error ? true : undefined}
          aria-busy={loading || undefined}
          className={cn(fieldBase, error && fieldError, loading && 'pr-10', className)}
        />
        {loading && (
          <span
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-4 w-4 animate-spin rounded-full border-2 border-line border-t-brand-400"
            aria-label={t.maps.loadingSdk}
            role="status"
          />
        )}
      </div>
      {error ? (
        <span className="text-xs text-err">{error}</span>
      ) : failed ? (
        <span className="text-xs text-ink-subtle">{t.maps.autocompleteUnavailable}</span>
      ) : hint ? (
        <span className="text-xs text-ink-subtle">{hint}</span>
      ) : null}
    </label>
  );
}
